import React, { useState } from 'react';
import './styles/OrderItemsCardMobile.css';

interface OrderItem {
  _id?: string;
  product?: {
    title?: string;
    imageUrl?: string;
  };
  variantTitle?: string;
  variantImage?: string;
  quantity?: number;
  price?: number;
}

interface OrderItemsCardMobileProps {
  items: OrderItem[];
  orderId?: string;
  totalAmount?: number;
  subtotal?: number;
  shippingCost?: number;
  totalGST?: number;
  couponDiscount?: number;
  appliedCredits?: number;
}

const OrderItemsCardMobile: React.FC<OrderItemsCardMobileProps> = ({ 
  items, 
  totalAmount,
  subtotal,
  shippingCost,
  totalGST,
  couponDiscount = 0,
  appliedCredits = 0
}) => {
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const [showModal, setShowModal] = useState(false);

  const handleImageError = (imageUrl: string) => {
    setImageErrors(prev => new Set(prev).add(imageUrl));
  };

  const getImageUrl = (item: OrderItem) => {
    const variantImage = item.variantImage;
    const productImage = item.product?.imageUrl;
    
    if (variantImage && !imageErrors.has(variantImage)) {
      return variantImage;
    }
    
    if (productImage && !imageErrors.has(productImage)) {
      return productImage;
    }
    
    return '/images/dummy.png';
  };

  const getItemName = (item: OrderItem) => {
    return item.variantTitle || item.product?.title || 'Product';
  };

  return (
    <>
      <div className="mobile-order-card" onClick={() => setShowModal(true)}>
        <div className="mobile-order-header">
          <h3 className="mobile-order-title">Items in this order</h3>
          <svg className="mobile-chevron" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M7.5 5L12.5 10L7.5 15" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        
        <div className="mobile-items-list">
          {items.map((item, index) => (
            <div key={index} className="mobile-item-row">
              <img 
                src={getImageUrl(item)}
                alt=""
                className="mobile-item-icon"
                onError={() => handleImageError(getImageUrl(item))}
              />
              <span className="mobile-item-text">
                {getItemName(item)} x {item.quantity || 1}
              </span>
            </div>
          ))}
        </div>

        <div className="mobile-order-footer">
          <span className="mobile-total-label">Total: ₹{totalAmount ? Math.round(totalAmount).toLocaleString() : '0'}</span>
          <span className="mobile-gst-label">incl. GST</span>
        </div>
      </div>

      {/* Details Modal */}
      {showModal && (
        <div className="mobile-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="mobile-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="mobile-modal-close" onClick={() => setShowModal(false)}>
              ✕
            </button>
            <h2 className="mobile-modal-title">Order Details</h2>
            
            <div className="mobile-modal-items-list">
              {items.map((item, index) => (
                <div key={index} className="mobile-modal-item-row">
                  <img
                    src={getImageUrl(item)}
                    alt={getItemName(item)}
                    className="mobile-modal-item-image"
                    onError={() => handleImageError(getImageUrl(item))}
                  />
                  <div className="mobile-modal-item-details">
                    <p className="mobile-modal-item-name">{getItemName(item)}</p>
                    <p className="mobile-modal-item-quantity">Quantity: {item.quantity || 1}</p>
                  </div>
                  <p className="mobile-modal-item-price">₹{item.price ? Math.round(item.price).toLocaleString() : '0'}</p>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="mobile-order-summary">
              <h3 className="mobile-summary-title">Order Summary</h3>
              
              <div className="mobile-summary-row">
                <span>Subtotal</span>
                <span>₹{subtotal ? Math.round(subtotal).toLocaleString() : '0'}</span>
              </div>

              {totalGST && totalGST > 0 && (
                <div className="mobile-summary-row">
                  <span>GST</span>
                  <span>₹{Math.round(totalGST).toLocaleString()}</span>
                </div>
              )}

              <div className="mobile-summary-row">
                <span>Delivery Charges</span>
                <span className={shippingCost && shippingCost > 0 ? 'mobile-delivery-charge' : 'mobile-free-delivery'}>
                  {shippingCost && shippingCost > 0 ? `₹${Math.round(shippingCost).toLocaleString()}` : 'Free'}
                </span>
              </div>

              {(couponDiscount || appliedCredits) && (
                <div className="mobile-summary-section">
                  {couponDiscount > 0 && (
                    <div className="mobile-summary-row">
                      <span>Discount Coupons</span>
                      <span className="mobile-coupon-value">-₹{Math.round(couponDiscount).toLocaleString()}</span>
                    </div>
                  )}
                  {appliedCredits > 0 && (
                    <div className="mobile-summary-row">
                      <span>BuildCoins Used</span>
                      <span className="mobile-coupon-value">-₹{Math.round(appliedCredits).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              )}

              {!couponDiscount && !appliedCredits && (
                <div className="mobile-summary-section">
                  <div className="mobile-summary-row">
                    <span>Discount Coupons</span>
                    <span className="mobile-coupon-value">-₹0</span>
                  </div>
                  <p className="mobile-summary-hint">Apply coupon code to get discount</p>
                </div>
              )}

              <div className="mobile-summary-divider"></div>

              <div className="mobile-summary-row mobile-total-row">
                <span>Total Amount</span>
                <span className="mobile-total-amount">₹{totalAmount ? Math.round(totalAmount).toLocaleString() : '0'}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrderItemsCardMobile;